import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Download, X } from 'lucide-react';

import { CsAsset } from '../../../../common/models/content-studio.model';

interface GenerationLightboxProps {
    asset: CsAsset | null;
    open: boolean;
    onClose: () => void;
}

/**
 * Fullscreen animated lightbox for viewing a generated asset.
 *
 * The image is rendered as a background-image with all right-click,
 * drag, and selection protection enabled. The lightbox uses framer-motion
 * layout animation so it smoothly expands from the GenerationNode card.
 *
 * Security note: this defeats casual users (no right-click save, no
 * drag-to-desktop, no "Copy image address"). It does NOT prevent
 * someone who opens DevTools → Network tab. For true protection, the
 * backend would need signed, expiring URLs — defer to a later PR.
 *
 * Press Escape or click the backdrop to close.
 */
export const GenerationLightbox: React.FC<GenerationLightboxProps> = ({
    asset,
    open,
    onClose,
}) => {
    const { t } = useTranslation();
    // Escape key
    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    const url = asset?.url || asset?.thumbnailUrl;
    const isVideo = asset?.kind === 'video';

    return (
        <AnimatePresence>
            {open && asset && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={onClose}
                        style={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 9998,
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                            cursor: 'zoom-out',
                        }}
                    />

                    {/* Content */}
                    <motion.div
                        layoutId={`node-gen-img-${asset.uuid}`}
                        initial={{ opacity: 0, scale: 0.88 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.92 }}
                        transition={{
                            type: 'spring',
                            stiffness: 300,
                            damping: 30,
                        }}
                        onClick={onClose}
                        style={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 9999,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 48,
                            cursor: 'zoom-out',
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            onContextMenu={(e) => e.preventDefault()}
                            onDragStart={(e) => e.preventDefault()}
                            className="relative cursor-default"
                        >
                            {/*
                              * The two controls float over the picture on a dark
                              * backdrop, so they answer to it rather than to the
                              * page theme — white on translucent white.
                              */}
                            <button
                                type="button"
                                onClick={async (e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    await downloadAsset(asset);
                                }}
                                title={t('CONTENT_STUDIO.ACTION_DOWNLOAD')}
                                aria-label={t('CONTENT_STUDIO.ACTION_DOWNLOAD')}
                                className="absolute -top-10 end-11 z-10 flex size-[34px] items-center justify-center rounded-full border border-white/50 bg-transparent text-white backdrop-blur-[4px] transition-all hover:border-white/90 hover:bg-white/15"
                            >
                                <Download className="size-[18px]" />
                            </button>

                            <button
                                type="button"
                                onClick={onClose}
                                className="absolute -top-10 end-0 z-10 flex size-[34px] items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-[4px] transition-colors hover:bg-white/30"
                            >
                                <X className="size-4" />
                            </button>

                            {isVideo && url ? (
                                <video
                                    src={url}
                                    controls
                                    autoPlay
                                    loop
                                    onContextMenu={(e) => e.preventDefault()}
                                    // No rounded corners in the lightbox — the goal is to see
                                    // the full frame edge-to-edge; rounding would hide detail.
                                    className="block max-h-[85vh] max-w-[90vw] object-contain"
                                />
                            ) : url ? (
                                <>
                                    <img
                                        src={url}
                                        alt=""
                                        draggable={false}
                                        onContextMenu={(e) => e.preventDefault()}
                                        onDragStart={(e) => e.preventDefault()}
                                        className="pointer-events-none block h-auto max-h-[85vh] w-auto max-w-[90vw] select-none"
                                    />
                                    <div
                                        onContextMenu={(e) => e.preventDefault()}
                                        onDragStart={(e) => e.preventDefault()}
                                        className="pointer-events-auto absolute inset-0"
                                    />
                                </>
                            ) : null}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

// ─── Download helpers ────────────────────────────────────────────────────

/**
 * Download the asset with a sensible filename. We use a blob + object-URL so
 * cross-origin assets on our CDN still honor the `download` attribute
 * (browsers ignore `download` on raw cross-origin anchor hrefs).
 *
 * Filename: recipeSlug-first8OfUuid.(ext) — easy to identify in the vendor's
 * downloads folder.
 */
async function downloadAsset(asset: CsAsset): Promise<void> {
    const sourceUrl = asset.url || asset.thumbnailUrl;
    if (!sourceUrl) return;

    const ext = extFromUrl(sourceUrl) || (asset.kind === 'video' ? 'mp4' : 'png');
    const baseName = `${asset.recipeSlug || 'asset'}-${asset.uuid.slice(0, 8)}.${ext}`;

    try {
        const res = await fetch(sourceUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = baseName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
        // Fallback: open the URL in a new tab — user can save from there.
        window.open(sourceUrl, '_blank', 'noopener');
    }
}

function extFromUrl(url: string): string | null {
    try {
        const path = new URL(url).pathname;
        const m = path.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
        return m ? m[1].toLowerCase() : null;
    } catch {
        return null;
    }
}
